import { Observable, of, defer } from "rxjs"
import { switchMap, map, catchError, filter, delay } from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import * as uuid from "uuid"
import { Router } from "../../../../server/router"
import type { EpicDependencies } from "../../../rxjs/configureEpics"
import {
    CLOSE_OPTION_GROUP_FORM_CLICKED,
    DELETE_OPTION_GROUP_CLICKED,
    OPTION_GROUP_FORM_SUBMITTED,
    OPTION_GROUP_LOCATION_SAVE_REQUESTED,
    OPTION_GROUP_LOCATION_SAVE_SUCCESS,
    OPTION_GROUP_SAVE_REQUESTED,
    OPTION_GROUP_SAVE_SUCCESS,
    optionGroupLocationSaveRequested,
    optionGroupLocationSaveSuccess,
    optionGroupSaveFailure,
    optionGroupSaveRequested,
    optionGroupSaveSuccess,
} from "./actions"
import { assignOptionGroupToProduct, createActionLoadingError } from "../../../data/actions"
import type { OptionGroupFormItem } from "./OptionGroupForm"
import { getSelectedLocationOverrideId } from "../../../common/locationsPicker/selectors"
import { getCurrentPath } from "../../../router/selectors"
import {
    createActionItemsAndCategoriesAgendaOptionGroupsLoaded,
    invalidateMenuData,
    ITEMS_CATEGORIES_AGENDA_WILL_MOUNT,
    MENU_DATA_INVALIDATED,
    shouldRefetchMenuDataEntity,
} from "../actions"
import { fetchOptionGroups, optionGroupDelete, optionGroupLocationSave, optionGroupSave } from "./request"
import { getHttpHeaders } from "../../../auth/selectors"
import { createLoadingEnded, createLoadingStarted } from "../../../filters/actions"
import { ACCOUNT_SELECTED, REFETCH_DATA } from "../../../auth/actions"
import { LOAD_AUTH_LOCATIONS_SUCCESS } from "../../../layouts/header/actions"
import { shouldDownloadItemsAndCategoriesData } from "../selectors"
import type {
    CategoryOptionGroup,
    CategoryOptionGroupOverride,
    OptionGroupLocationSaveRequestData,
    OptionGroupSaveRequestData,
} from "../../../../graphql/types"
import { sendOperationsClearState } from "../../../operations/actions"
import { shouldAddNewGroupToItem } from "../modifiers/selectors"
import { getOptionGroupById } from "./selectors"
import { createOptionGroupPayload } from "./utlis"
import { logger } from "../../../../utils/loggerWrap"
import { Action, StateObject } from "../../../redux/types"
import { constructAuditData } from "../../auditLog/utils/utils-payload-data"

export const optionGroupSubmittedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(OPTION_GROUP_FORM_SUBMITTED),
        switchMap((action: Action) => {
            logger.info("optionGroupSubmittedEpics - form submitted", {
                data: action.payload,
            })

            if (!action.payload.oldValue) {
                // New
                const optionGroup: OptionGroupFormItem = action.payload.newValue
                const data: OptionGroupSaveRequestData = {
                    id: deps.idgen.next(),
                    name: optionGroup.name,
                    displayName: optionGroup.displayName,
                    displayType: optionGroup.display,
                    isActive: optionGroup.isActive,
                    options: optionGroup.items.map(item => ({
                        id: uuid.v4(),
                        productId: item.optionId,
                        name: item.name,
                        kitchenName: item.kitchenName,
                    })),
                    isCreating: true,
                }
                return [optionGroupSaveRequested(data)]
            } else {
                const selectedLocationId = getSelectedLocationOverrideId(deps.getState())

                if (selectedLocationId) {
                    const optionGroup: OptionGroupFormItem = action.payload.newValue
                    const data: OptionGroupLocationSaveRequestData = {
                        // @ts-expect-error
                        id: optionGroup.optionGroupId,
                        locationId: selectedLocationId,
                        isActive: optionGroup.isActive,
                    }
                    return [optionGroupLocationSaveRequested(data, selectedLocationId)]
                } else {
                    const optionGroup: OptionGroupFormItem = action.payload.newValue
                    const data: OptionGroupSaveRequestData = {
                        // @ts-expect-error
                        id: optionGroup.optionGroupId,
                        name: optionGroup.name,
                        displayName: optionGroup.displayName,
                        displayType: optionGroup.display,
                        isActive: optionGroup.isActive,
                        options: optionGroup.items.map(item => ({
                            id: item.groupItemId || deps.idgen.next(),
                            productId: item.optionId,
                            name: item.name,
                            kitchenName: item.kitchenName,
                        })),
                    }
                    return [optionGroupSaveRequested(data)]
                }
            }
        })
    )

export const optionGroupSaveRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(OPTION_GROUP_SAVE_REQUESTED),
        switchMap((action: Action) => {
            const data: OptionGroupSaveRequestData = {
                ...action.payload.data,
                auditData: constructAuditData(deps, true),
            }
            return defer(() => optionGroupSave(getHttpHeaders(deps.getState()), data)).pipe(
                switchMap(() => {
                    const payloadGroup: OptionGroupSaveRequestData = action.payload.data
                    const originGroup = getOptionGroupById(deps.getState(), payloadGroup.id)
                    const categoryOptionGroup: CategoryOptionGroup = createOptionGroupPayload(payloadGroup, originGroup)

                    // Do not invalidate here: the open item detail must stay cached or its form
                    // unmounts and the pending assignment is lost.
                    if (shouldAddNewGroupToItem(deps.getState())) {
                        return [
                            assignOptionGroupToProduct(categoryOptionGroup.id),
                            optionGroupSaveSuccess(categoryOptionGroup),
                        ]
                    }

                    return [
                        optionGroupSaveSuccess(categoryOptionGroup),
                        invalidateMenuData(["itemsList", "itemDetails"]),
                    ]
                }),
                catchError(error => of(optionGroupSaveFailure(error)))
            )
        })
    )

export const optionGroupLocationSaveRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(OPTION_GROUP_LOCATION_SAVE_REQUESTED),
        switchMap((action: Action) => {
            const data: OptionGroupLocationSaveRequestData = {
                ...action.payload.data,
                auditData: constructAuditData(deps, true),
            }
            return defer(() => optionGroupLocationSave(getHttpHeaders(deps.getState()), data)).pipe(
                switchMap(response => {
                    if (response) {
                        const payloadGroup: OptionGroupSaveRequestData = action.payload.data
                        const originGroup = getOptionGroupById(deps.getState(), payloadGroup.id)

                        if (!originGroup) {
                            return []
                        }

                        const categoryOptionGroup: CategoryOptionGroupOverride = {
                            locationId: action.payload.locationId,
                            data: {
                                id: originGroup.id,
                                price: originGroup.price,
                                isActive: payloadGroup.isActive,
                            },
                        }
                        return [
                            optionGroupLocationSaveSuccess(
                                categoryOptionGroup,
                                action.payload.data,
                                action.payload.locationId
                            ),
                        ]
                    }

                    return []
                }),
                catchError(error => of(optionGroupSaveFailure(error)))
            )
        })
    )

export const deleteOptionGroupSubmittedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(DELETE_OPTION_GROUP_CLICKED),
        switchMap((action: Action) => {
            logger.info("deleteOptionGroupSubmittedEpics - form submitted", {
                data: action.payload,
            })

            return defer(() =>
                optionGroupDelete(
                    getHttpHeaders(deps.getState()),
                    action.payload.optionGroupId,
                    constructAuditData(deps, true)
                )
            ).pipe(
                switchMap(() => [invalidateMenuData(["itemsList", "itemDetails"])]),
                catchError(error => of(createActionLoadingError(error)))
            )
        })
    )

export const createCategoryGroupSubmittedRouterEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(OPTION_GROUP_FORM_SUBMITTED, CLOSE_OPTION_GROUP_FORM_CLICKED, DELETE_OPTION_GROUP_CLICKED),
        switchMap((action: Action) => {
            const currentUrlQuery = getCurrentPath(deps.getState()).query
            const categoryId = currentUrlQuery.id
            const tab = currentUrlQuery.tab

            if (action.type === "option-group/close/clicked" && tab === "categories" && categoryId) {
                Router.push(`/menu/categories/edit/${categoryId}`)
                return [assignOptionGroupToProduct(null)]
            }

            if (action.type === "option-group/close/clicked" && tab === "schematized" && categoryId) {
                Router.push(`/sales-input/schematized/edit/${categoryId}`)
                return [assignOptionGroupToProduct(null)]
            }

            if (tab === "categories" && categoryId) {
                Router.push(`/menu/categories/edit/${categoryId}`)
                return []
            } else if (tab === "categories" && !categoryId) {
                Router.push("/menu/categories/add/product")
                return []
            }

            if (tab === "schematized" && categoryId) {
                Router.push(`/sales-input/schematized/edit/${categoryId}`)
                return []
            }

            Router.push("/menu/option-groups")
            return []
        })
    )

export const mounted1Epic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(
            ITEMS_CATEGORIES_AGENDA_WILL_MOUNT,
            ACCOUNT_SELECTED,
            REFETCH_DATA,
            LOAD_AUTH_LOCATIONS_SUCCESS,
            MENU_DATA_INVALIDATED
        ),
        filter((action: Action) => shouldRefetchMenuDataEntity(action, "optionGroups")),
        filter(() => shouldDownloadItemsAndCategoriesData(deps.getState())),
        map(() => createLoadingStarted("optionGroups"))
    )

export const mounted2Epic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(
            ITEMS_CATEGORIES_AGENDA_WILL_MOUNT,
            ACCOUNT_SELECTED,
            REFETCH_DATA,
            LOAD_AUTH_LOCATIONS_SUCCESS,
            MENU_DATA_INVALIDATED
        ),
        filter((action: Action) => shouldRefetchMenuDataEntity(action, "optionGroups")),
        filter(() => shouldDownloadItemsAndCategoriesData(deps.getState())),
        switchMap(() =>
            defer(() => fetchOptionGroups(getHttpHeaders(deps.getState()))).pipe(
                switchMap(result => {
                    if (!result) {
                        return [createLoadingEnded("optionGroups")]
                    }

                    return [
                        createLoadingEnded("optionGroups"),
                        createActionItemsAndCategoriesAgendaOptionGroupsLoaded(result),
                    ]
                }),
                catchError(error => of(createActionLoadingError(error)))
            )
        )
    )

export const clearStatusStateEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(OPTION_GROUP_SAVE_SUCCESS, OPTION_GROUP_LOCATION_SAVE_SUCCESS),
        delay(2000),
        map(() => sendOperationsClearState())
    )

export default [
    optionGroupLocationSaveRequestedEpics,
    clearStatusStateEpic,
    optionGroupSaveRequestedEpics,
    deleteOptionGroupSubmittedEpics,
    optionGroupSubmittedEpics,
    createCategoryGroupSubmittedRouterEpics,
    mounted1Epic,
    mounted2Epic,
]
